import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { BarChart3, CalendarDays, LineChart as LineChartIcon, Settings2, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { cn } from '@/shared/utils';

export const COMPARATIVE_PRESENT_PAST_CARD_CHART = 'Comparative_Present-Past_Card_Chart';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today', pastLabel: 'Yesterday' },
  { value: 'week', label: 'This week', pastLabel: 'Past week' },
  { value: 'month', label: 'This month', pastLabel: 'Past month' }
];

const MODE_OPTIONS = [
  { value: 'total', label: 'Total', shortLabel: 'T' },
  { value: 'accumulative', label: 'Accumulative', shortLabel: 'A' }
];

const CHART_OPTIONS = [
  { value: 'line', label: 'Line', icon: LineChartIcon },
  { value: 'multi-line', label: 'Multi-line', icon: LineChartIcon },
  { value: 'bar', label: 'Bars', icon: BarChart3 },
  { value: 'area', label: 'Area', icon: BarChart3 }
];

const ACCENT_BORDER_CLASS = {
  teal: 'border-l-teal-500',
  orange: 'border-l-orange-500',
  blue: 'border-l-blue-500',
  green: 'border-l-emerald-500',
  emerald: 'border-l-emerald-500',
  amber: 'border-l-amber-500',
  red: 'border-l-red-500',
  purple: 'border-l-purple-500',
  violet: 'border-l-purple-500'
};

const ICON_TONE_CLASS = {
  teal: 'bg-teal-500/10 text-teal-600 dark:text-teal-300',
  orange: 'bg-orange-500/10 text-orange-600 dark:text-orange-300',
  blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-300',
  green: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-300',
  red: 'bg-red-500/10 text-red-600 dark:text-red-300',
  purple: 'bg-purple-500/10 text-purple-600 dark:text-purple-300',
  violet: 'bg-purple-500/10 text-purple-600 dark:text-purple-300'
};

const CHART_TONE_CLASS = {
  teal: 'text-teal-600 dark:text-teal-300',
  orange: 'text-orange-600 dark:text-orange-300',
  blue: 'text-blue-600 dark:text-blue-300',
  green: 'text-emerald-600 dark:text-emerald-300',
  emerald: 'text-emerald-600 dark:text-emerald-300',
  amber: 'text-amber-600 dark:text-amber-300',
  red: 'text-red-600 dark:text-red-300',
  purple: 'text-purple-600 dark:text-purple-300',
  violet: 'text-purple-600 dark:text-purple-300'
};

const defaultFormatter = (value) => new Intl.NumberFormat('en-US').format(value || 0);

const compactFormatter = (value) => {
  const numeric = Number(value || 0);
  if (Math.abs(numeric) >= 1000000) return `${(numeric / 1000000).toFixed(1)}m`;
  if (Math.abs(numeric) >= 1000) return `${(numeric / 1000).toFixed(1)}k`;
  return `${numeric}`;
};

const cumulative = (items) => {
  let total = 0;
  return items.map((item) => {
    total += Number(item.value || 0);
    return { ...item, value: total };
  });
};

const normalizeSeries = (present = [], past = [], mode = 'total') => {
  const presentSeries = mode === 'accumulative' ? cumulative(present) : present;
  const pastSeries = mode === 'accumulative' ? cumulative(past) : past;
  const length = Math.max(presentSeries.length, pastSeries.length, 1);

  return Array.from({ length }, (_, index) => ({
    label: presentSeries[index]?.label || pastSeries[index]?.label || `${index + 1}`,
    present: Number(presentSeries[index]?.value || 0),
    past: Number(pastSeries[index]?.value || 0)
  }));
};

const totalFor = (items = [], mode = 'total') => {
  if (!items.length) return 0;
  if (mode === 'accumulative') return cumulative(items).at(-1)?.value || 0;
  return items.reduce((sum, item) => sum + Number(item.value || 0), 0);
};

const optionClassName = (active) => cn(
  'rounded-md border px-2 py-1 text-xs font-semibold transition-colors',
  active
    ? 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-950'
    : 'border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
);

export function ComparativePresentPastCardChart({
  title,
  valueLabel,
  subtitle,
  icon: Icon = CalendarDays,
  presentData = [],
  pastData = [],
  period = 'today',
  comparisonMode = 'total',
  chartType = 'line',
  color = 'emerald',
  valueFormatter = defaultFormatter,
  className,
  onSettingsChange
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activePeriod, setActivePeriod] = useState(period);
  const [activeMode, setActiveMode] = useState(comparisonMode);
  const [activeChart, setActiveChart] = useState(chartType);

  const chartData = useMemo(
    () => normalizeSeries(presentData, pastData, activeMode),
    [presentData, pastData, activeMode]
  );

  const presentTotal = useMemo(() => totalFor(presentData, activeMode), [presentData, activeMode]);
  const pastTotal = useMemo(() => totalFor(pastData, activeMode), [pastData, activeMode]);
  const delta = pastTotal === 0 ? (presentTotal > 0 ? 100 : 0) : ((presentTotal - pastTotal) / pastTotal) * 100;
  const isPositive = delta >= 0;
  const periodMeta = PERIOD_OPTIONS.find((item) => item.value === activePeriod) || PERIOD_OPTIONS[0];
  const modeMeta = MODE_OPTIONS.find((item) => item.value === activeMode) || MODE_OPTIONS[0];
  const accentBorderClass = ACCENT_BORDER_CLASS[color] || ACCENT_BORDER_CLASS.emerald;
  const iconToneClass = ICON_TONE_CLASS[color] || ICON_TONE_CLASS.emerald;
  const chartToneClass = CHART_TONE_CLASS[color] || CHART_TONE_CLASS.emerald;

  const applySettings = (next) => {
    if (next.period) setActivePeriod(next.period);
    if (next.mode) setActiveMode(next.mode);
    if (next.chart) setActiveChart(next.chart);
    onSettingsChange?.({
      period: next.period || activePeriod,
      comparisonMode: next.mode || activeMode,
      chartType: next.chart || activeChart
    });
  };

  const ChartShell = () => {
    if (activeChart === 'bar') {
      return (
        <BarChart data={chartData} margin={{ top: 2, right: 0, left: -30, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" opacity={0.12} />
          <XAxis dataKey="label" hide />
          <YAxis hide domain={[0, 'dataMax']} />
          <Tooltip formatter={(value) => compactFormatter(value)} />
          <Bar dataKey="past" radius={[3, 3, 0, 0]} fill="currentColor" opacity={0.28} />
          <Bar dataKey="present" radius={[3, 3, 0, 0]} fill="currentColor" opacity={0.92} />
        </BarChart>
      );
    }

    if (activeChart === 'area') {
      return (
        <AreaChart data={chartData} margin={{ top: 2, right: 0, left: -30, bottom: 0 }}>
          <XAxis dataKey="label" hide />
          <YAxis hide domain={[0, 'dataMax']} />
          <Tooltip formatter={(value) => compactFormatter(value)} />
          <Area type="monotone" dataKey="past" stroke="currentColor" fill="currentColor" fillOpacity={0.08} strokeOpacity={0.28} strokeWidth={2} />
          <Area type="monotone" dataKey="present" stroke="currentColor" fill="currentColor" fillOpacity={0.18} strokeWidth={2.5} />
        </AreaChart>
      );
    }

    return (
      <LineChart data={chartData} margin={{ top: 2, right: 0, left: -30, bottom: 0 }}>
        <XAxis dataKey="label" hide />
        <YAxis hide domain={[0, 'dataMax']} />
        <Tooltip formatter={(value) => compactFormatter(value)} />
        <Line type="monotone" dataKey="past" dot={false} stroke="currentColor" strokeOpacity={0.28} strokeWidth={2} />
        <Line type="monotone" dataKey="present" dot={activeChart === 'multi-line'} stroke="currentColor" strokeWidth={2.5} />
      </LineChart>
    );
  };

  return (
    <>
      <Card
        className={cn(
          'card-elevated overflow-hidden border-l-4 bg-card transition-all duration-200 hover:shadow-lg',
          'min-h-[156px] max-h-[204px]',
          accentBorderClass,
          className
        )}
        data-card-type={COMPARATIVE_PRESENT_PAST_CARD_CHART}
      >
        <CardContent className="flex h-full flex-col p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                iconToneClass
              )}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs font-medium uppercase text-muted-foreground">{title}</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold leading-tight">{valueLabel ?? valueFormatter(presentTotal)}</p>
                  <span className={cn('flex items-center gap-0.5 text-[11px] font-semibold', isPositive ? 'text-emerald-600' : 'text-red-600')}>
                    {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.abs(delta).toFixed(1)}%
                  </span>
                </div>
                <p className="truncate text-[11px] text-muted-foreground">{subtitle || `${periodMeta.label} vs ${periodMeta.pastLabel}`}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                className="h-6 rounded-md border border-slate-200 px-1.5 text-[10px] font-bold text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                title={`Mode: ${modeMeta.label}`}
                onClick={() => applySettings({ mode: activeMode === 'total' ? 'accumulative' : 'total' })}
              >
                {modeMeta.shortLabel}
              </button>
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                title="Card settings"
                aria-label="Card settings"
                onClick={() => setSettingsOpen(true)}
              >
                <Settings2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className={cn('mt-3 h-[58px]', chartToneClass)}>
            <ResponsiveContainer width="100%" height="100%">
              <ChartShell />
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Card Settings</DialogTitle>
            <DialogDescription>{COMPARATIVE_PRESENT_PAST_CARD_CHART}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Period</p>
              <div className="flex flex-wrap gap-2">
                {PERIOD_OPTIONS.map((item) => (
                  <button key={item.value} type="button" className={optionClassName(activePeriod === item.value)} onClick={() => applySettings({ period: item.value })}>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Comparison</p>
              <div className="flex flex-wrap gap-2">
                {MODE_OPTIONS.map((item) => (
                  <button key={item.value} type="button" className={optionClassName(activeMode === item.value)} onClick={() => applySettings({ mode: item.value })}>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Chart</p>
              <div className="grid grid-cols-2 gap-2">
                {CHART_OPTIONS.map((item) => {
                  const OptionIcon = item.icon;
                  return (
                    <button key={item.value} type="button" className={cn(optionClassName(activeChart === item.value), 'flex items-center justify-center gap-2')} onClick={() => applySettings({ chart: item.value })}>
                      <OptionIcon className="h-3.5 w-3.5" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ComparativePresentPastCardChart;
